import { Injectable } from '@angular/core';
import { WorkspaceStateManagerService } from '../workspace-state-manager.service';
import { BehaviorSubject } from 'rxjs';
import { AssetType } from '../../../../models/defines';
import { WorkspaceAssetEventListenerService } from '../../../state-management/workspace/socket/listeners/workspace-asset-event-listener.service';
import { WorkspaceAssetApiManagerService } from '../../api/managers/associations/workspace-asset-api-manager.service';
import { ILocalAsset } from '../../../../models/assets/assets.model';
import { assetConverter } from '../../../../models/assets/assets-converter';
import { IPaginationMeta } from '../../api/associations/workspace-asset-request.types';

interface IPaginationCursors {
    folderPage: number;
    assetPage: number;
    limit: number;
}

@Injectable()
export class WorkspaceAssetStateManagerService {
    public onGeneratedVideo$ = this.workspaceAssetEventListener.onGeneratedVideo$;

    private _foldersSubject = new BehaviorSubject<ILocalAsset[]>(null);
    public folders$ = this._foldersSubject.asObservable();

    private _foldersPaginationSubject = new BehaviorSubject<IPaginationMeta | null>(null);
    public foldersPagination$ = this._foldersPaginationSubject.asObservable();

    // ── Assets (root + inside folder) ────────────────────────────────────────
    private _assetsSubject = new BehaviorSubject<ILocalAsset[]>(null);
    public assets$ = this._assetsSubject.asObservable();

    private _assetsPaginationSubject = new BehaviorSubject<IPaginationMeta | null>(null);
    public assetsPagination$ = this._assetsPaginationSubject.asObservable();

    // ── Status ───────────────────────────────────────────────────────────────
    private _isLoadingSubject = new BehaviorSubject<boolean>(false);
    public isLoading$ = this._isLoadingSubject.asObservable();

    private _isFoldersLoadingSubject = new BehaviorSubject<boolean>(false);
    public isFoldersLoading$ = this._isFoldersLoadingSubject.asObservable();

    private _isAssetsLoadingSubject = new BehaviorSubject<boolean>(false);
    public isAssetsLoading$ = this._isAssetsLoadingSubject.asObservable();

    private _errorSubject = new BehaviorSubject<string | null>(null);
    public error$ = this._errorSubject.asObservable();
    private _currentFolderNameSubject = new BehaviorSubject<string | null>('/');
    public currentFolderName$ = this._currentFolderNameSubject.asObservable();

    private currentWorkspaceId: string | null = null;
    private workspaceGeneration = 0;
    private filterType: AssetType | null = null;
    private cursors: IPaginationCursors = {
        folderPage: 1,
        assetPage: 1,
        limit: 10,
    };

    constructor(
        private wsStateService: WorkspaceStateManagerService,
        private workspaceAssetEventListener: WorkspaceAssetEventListenerService,
        private workspaceAssetApiManager: WorkspaceAssetApiManagerService
    ) {
        this.wsStateService.currentWorkspaceId$.subscribe((id) => {
            if (!id) {
                this._reset();
                return;
            }
            if (this.currentWorkspaceId === id) return;
            this._reset();
            this.currentWorkspaceId = id;
            this.loadAssetsAsync();
        });

        this.workspaceAssetEventListener.onAssetDeleted$.subscribe((data) => {
            if (!data) return;
            this.loadAssetsAsync();
        });
        this.workspaceAssetEventListener.onAssetCreated$.subscribe((data) => {
            if (!data) return;
            this.loadAssetsAsync();
        });
        this.workspaceAssetEventListener.onAssetUpdated$.subscribe((data) => {
            if (!data) return;
            this.loadAssetsAsync();
        });
    }

    public enterFolder(folderName: string): void {
        this._currentFolderNameSubject.next(folderName);
        this.cursors.assetPage = 1;
        this.loadAssetsAsync();
    }

    public exitFolder(): void {
        this._currentFolderNameSubject.next('/');
        this.cursors.folderPage = 1;
        this.cursors.assetPage = 1;
        this.loadAssetsAsync();
    }

    public setFolderPage(page: number): void {
        this.cursors.folderPage = page;
        this._isFoldersLoadingSubject.next(true);
        this.loadAssetsAsync('folders');
    }

    public setAssetPage(page: number): void {
        this.cursors.assetPage = page;
        this._isAssetsLoadingSubject.next(true);
        this.loadAssetsAsync('assets');
    }

    public setFilterType(filterType: AssetType | null): void {
        this.filterType = filterType;
        this.cursors.folderPage = 1;
        this.cursors.assetPage = 1;
        this.loadAssetsAsync();
    }

    public getCurrentFolderName(): string {
        return this._currentFolderNameSubject.value;
    }

    public async loadAssetsAsync(updateOnly?: 'folders' | 'assets'): Promise<void> {
        if (!this.currentWorkspaceId) {
            return;
        }
        const generation = this.workspaceGeneration;
        this._isLoadingSubject.next(true);
        this._errorSubject.next(null);

        const { data, error } = await this.workspaceAssetApiManager.getAssetsInWorkspaceAsync({
            limit: this.cursors.limit,
            filterByType: this.filterType,
            folderName: this.getCurrentFolderName(),
            folderPage: this.cursors.folderPage,
            assetPage: this.cursors.assetPage,
        });

        if (generation !== this.workspaceGeneration) return;

        this._isLoadingSubject.next(false);
        this._isFoldersLoadingSubject.next(false);
        this._isAssetsLoadingSubject.next(false);

        if (error) {
            console.error(`Failed to load assets: ${error.message}`);
            this._errorSubject.next('An error occurred when trying to get assets for workspace');
            return;
        }

        if (!updateOnly || updateOnly === 'folders') {
            this._foldersSubject.next(assetConverter.convertArrayOfInAssetsToLocal(data.folders.items));
            this._foldersPaginationSubject.next(data.pagination.folders);
        }
        if (!updateOnly || updateOnly === 'assets') {
            this._assetsSubject.next(assetConverter.convertArrayOfInAssetsToLocal(data.assets.items));
            this._assetsPaginationSubject.next(data.pagination.assets);
        }
    }
    public getCurrentFolders(): ILocalAsset[] {
        return this._foldersSubject.value;
    }
    // ── Helpers ──────────────────────────────────────────────────────────────

    private _reset(): void {
        ++this.workspaceGeneration;
        this.currentWorkspaceId = null;
        this._currentFolderNameSubject.next('/');
        this.filterType = null;
        this.cursors = { folderPage: 1, assetPage: 1, limit: 10 };
        this._foldersSubject.next(null);
        this._assetsSubject.next(null);
        this._foldersPaginationSubject.next(null);
        this._assetsPaginationSubject.next(null);
        this._errorSubject.next(null);
        this._isLoadingSubject.next(false);
        this._isFoldersLoadingSubject.next(false);
        this._isAssetsLoadingSubject.next(false);
    }
}
